import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { usePermissions, RESOURCES } from '@/contexts/PermissionsContext';
import { Pagination } from '@/components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth helper for API calls
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

// Get date range from current month 1st to today
const getDefaultDateRange = () => {
  const now = new Date();
  // Current month 1st
  const currentMonth1st = new Date(now.getFullYear(), now.getMonth(), 1);
  // Today
  const today = now;
  
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  return { from: formatLocalDate(currentMonth1st), to: formatLocalDate(today) };
};

// Get date range for a specific month
const getMonthDateRange = (monthOffset = 0) => {
  const now = new Date();
  const targetMonth = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + monthOffset + 1, 0);
  
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // For current month, use today as end date
  const endDate = monthOffset === 0 ? now : lastDay;
  
  return { from: formatLocalDate(targetMonth), to: formatLocalDate(endDate) };
};

// Get month name for tabs
const getMonthName = (monthOffset = 0) => {
  const now = new Date();
  const targetDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  return targetDate.toLocaleString('default', { month: 'long', year: 'numeric' });
};

// All available columns - sorted alphabetically by label
const ALL_COLUMNS = [
  // Default columns (sorted alphabetically)
  { id: 'accounts_verified_by', label: 'Accounts Verified By', default: false },
  { id: 'address_proof_status', label: 'Address Proof Status', default: false },
  { id: 'battery_no', label: 'Battery No.', default: false },
  { id: 'booking_no', label: 'Booking No', default: true },
  { id: 'charger_no', label: 'Charger No.', default: false },
  { id: 'chassis_no', label: 'Chassis No', default: true },
  { id: 'customer_balance', label: 'Customer Balance', default: true },
  { id: 'customer_name', label: 'Customer Name', default: true },
  { id: 'date_of_booking', label: 'Booking Date', default: true },
  { id: 'date_of_registration', label: 'Date of Registration', default: false },
  { id: 'delivery_date', label: 'Delivery Date', default: false },
  { id: 'dms_invoice_date', label: 'DMS Invoice Date', default: true },
  { id: 'engine_motor_no', label: 'Engine/Motor No.', default: false },
  { id: 'exchange_vehicle_status', label: 'Exchange Inward Status', default: false },
  { id: 'ex_showroom_price', label: 'Ex-Showroom Price', default: false },
  { id: 'financier_name', label: 'Financier', default: false },
  { id: 'hsrp_informed_date', label: 'HSRP Received Informed', default: false },
  { id: 'hsrp_number_plate', label: 'HSRP Number Plate', default: false },
  { id: 'invoice_dms_status', label: 'Invoice DMS Status', default: false },
  { id: 'ledger_status', label: 'Ledger Status', default: false },
  { id: 'loan_status', label: 'Loan Status', default: false },
  { id: 'mobile_no_1', label: 'Mobile No', default: true },
  { id: 'on_road_price', label: 'On Road Price', default: false },
  { id: 'photo_status', label: 'Photo Status', default: false },
  { id: 'pme_drive_no', label: 'PME Drive', default: true },
  { id: 'pme_status', label: 'PME Status', default: false },
  { id: 'pme_submission_date', label: 'PME Submission Date', default: false },
  { id: 'policy_status', label: 'Policy Status', default: false },
  { id: 'pool_amount', label: 'Pool Amount', default: false },
  { id: 'rc_status', label: 'RC Status', default: false },
  { id: 'registration_number', label: 'Registration No', default: true },
  { id: 'rto_paper_status', label: 'RTO Paper Status', default: false },
  { id: 'sales_representative', label: 'Sales Rep', default: true },
  { id: 'scheme_discount_1', label: 'Scheme Discount 1', default: false },
  { id: 'scheme_discount_2', label: 'Scheme Discount 2', default: false },
  { id: 'special_discount', label: 'Special Discount', default: false },
  { id: 'tally_invoice', label: 'Tally Invoice', default: false },
  { id: 'vehicle', label: 'Model, Variant, Color', default: true },
  { id: 'vendors_purchase_price', label: 'Vendor Purchase Price', default: false },
];

// Bulk Edit Fields
const BULK_EDIT_FIELDS = [
  { id: 'delivery_date', label: 'Delivery Date', type: 'date' },
  { id: 'date_of_registration', label: 'Date of Registration', type: 'date' },
  { id: 'hsrp_informed_date', label: 'HSRP Received Informed', type: 'date' },
  { id: 'hsrp_number_plate', label: 'HSRP Number Plate', type: 'select', options: ['Pending', 'Applied', 'Received'] },
  { id: 'rto_paper_status', label: 'RTO Paper Status', type: 'select', options: ['Pending', 'Submitted', 'Received'] },
  { id: 'rc_status', label: 'RC Status', type: 'select', options: ['Pending', 'Applied', 'Received'] },
  { id: 'ledger_status', label: 'Ledger Status', type: 'select', options: ['Pending', 'Updated', 'Verified'] },
  { id: 'pme_status', label: 'PME Status', type: 'select', options: ['Pending', 'Submitted', 'Approved', 'Rejected'] },
  { id: 'invoice_dms_status', label: 'Invoice DMS Status', type: 'select', options: ['Pending', 'Completed'] },
  { id: 'tally_invoice', label: 'Tally Invoice', type: 'text' },
  { id: 'accounts_verified_by', label: 'Accounts Verified By', type: 'select', options: ['Jr.Accountant', 'Sr.Accountant', 'Chief Accountant'] },
  { id: 'pool_amount', label: 'Pool Amount', type: 'number' },
  { id: 'scheme_discount_1', label: 'Scheme Discount 1 (₹)', type: 'number' },
  { id: 'scheme_discount_2', label: 'Scheme Discount 2 (₹)', type: 'number' },
  { id: 'special_discount', label: 'Special Discount (₹)', type: 'number' },
  { id: 'exchange_vehicle_status', label: 'Exchange Vehicle Inward Status', type: 'select', options: ['Pending', 'Received', 'NA'] },
  { id: 'address_proof_status', label: 'Address Proof Status', type: 'select', options: ['Pending', 'Received', 'NA'] },
  { id: 'photo_status', label: 'Photo Status', type: 'select', options: ['Pending', 'Received', 'NA'] },
  { id: 'pme_submission_date', label: 'PME Submission Date', type: 'date' },
  { id: 'loan_status', label: 'Loan Status', type: 'select', options: ['Cash', 'Finance', 'Pending'] },
  { id: 'sales_representative', label: 'Sales Representative', type: 'master', masterKey: 'salesReps' },
];

function EnhancedBookingsList({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canDelete, canUpdate, canCreate, isViewer } = usePermissions();
  const [allBookings, setAllBookings] = React.useState([]);
  const [filteredBookings, setFilteredBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [selectedBookings, setSelectedBookings] = React.useState([]);
  const [masters, setMasters] = React.useState({ salesReps: [], models: [], variants: [], brands: [] });
  const itemsPerPage = 10;
  
  // Month Tab State - 'current' or 'previous'
  const [activeMonthTab, setActiveMonthTab] = React.useState('current');
  
  // Column selection
  const [selectedColumns, setSelectedColumns] = React.useState(
    ALL_COLUMNS.filter(c => c.default).map(c => c.id)
  );
  const [showColumnSelector, setShowColumnSelector] = React.useState(false);
  
  // Filters - always visible, date range from prev month 1st to today, filter by date_of_booking
  const defaultDateRange = getDefaultDateRange();
  const [selectAllBookings, setSelectAllBookings] = React.useState(false);
  const [filters, setFilters] = React.useState({
    dateFrom: defaultDateRange.from,
    dateTo: defaultDateRange.to,
    bookingNo: '',
    customerName: '',
    mobile: '',
    registrationNo: '',
    chassisNo: '',
    engineMotorNo: '',
    chargerNo: '',
    batteryNo: '',
    salesRep: '',
    vehicleSearch: '',
    brands: [],
    models: [],
    salesReps: []
  });
  // Filters always expanded - removed showFilters toggle
  
  // Inline Edit
  const [editingCell, setEditingCell] = React.useState(null);
  const [editValue, setEditValue] = React.useState('');
  
  // Bulk Edit Modal
  const [showBulkEditModal, setShowBulkEditModal] = React.useState(false);
  const [bulkEditField, setBulkEditField] = React.useState('');
  const [bulkEditValue, setBulkEditValue] = React.useState('');
  
  // Export Modal
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [exportFields, setExportFields] = React.useState([]);
  const [exportType, setExportType] = React.useState('selected'); // 'all' or 'selected'
  const [shareEmail, setShareEmail] = React.useState('');
  const [sharePhone, setSharePhone] = React.useState('');
  const [sendingEmail, setSendingEmail] = React.useState(false);
  const [generatingLink, setGeneratingLink] = React.useState(false);
  
  // Booking Action Modal - for Edit/View/Delete from Booking No click
  const [showActionModal, setShowActionModal] = React.useState(false);
  const [selectedActionBooking, setSelectedActionBooking] = React.useState(null);

  const handleBookingNoClick = (booking) => {
    setSelectedActionBooking(booking);
    setShowActionModal(true);
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/bookings/${bookingId}`, getAuthHeader());
      toast.success('Booking deleted successfully');
      setShowActionModal(false);
      setSelectedActionBooking(null);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to delete booking: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handle month tab change
  const handleMonthTabChange = (tab) => {
    setActiveMonthTab(tab);
    const dateRange = getMonthDateRange(tab === 'current' ? 0 : -1);
    setFilters(prev => ({
      ...prev,
      dateFrom: dateRange.from,
      dateTo: dateRange.to
    }));
    setCurrentPage(1);
  };

  React.useEffect(() => {
    fetchBookings();
    fetchAllMasters();
    setExportFields(selectedColumns);
  }, []);

  React.useEffect(() => {
    applyFilters();
  }, [filters, allBookings, selectAllBookings]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings?limit=10000`, getAuthHeader());
      const sorted = response.data.sort((a, b) => {
        const dateA = a.date_of_booking ? new Date(a.date_of_booking) : new Date(0);
        const dateB = b.date_of_booking ? new Date(b.date_of_booking) : new Date(0);
        if (dateB.getTime() !== dateA.getTime()) return dateB - dateA;
        return (parseInt(b.booking_no) || 0) - (parseInt(a.booking_no) || 0);
      });
      setAllBookings(sorted);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    }
    setLoading(false);
  };

  const fetchAllMasters = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters`, getAuthHeader());
      const mastersData = response.data;
      setMasters({
        salesReps: mastersData.filter(m => m.type === 'sales_rep').map(m => m.value),
        models: mastersData.filter(m => m.type === 'model').map(m => m.value),
        variants: mastersData.filter(m => m.type === 'variant').map(m => m.value),
        brands: mastersData.filter(m => m.type === 'brand').map(m => m.value)
      });
    } catch (error) {
      console.error('Failed to fetch masters');
    }
  };

  const applyFilters = () => {
    let filtered = [...allBookings];

    // If "Select All Bookings" is checked, skip date filters
    if (!selectAllBookings) {
      // Filter by date_of_booking (not dms_invoice_date)
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        filtered = filtered.filter(b => {
          if (!b.date_of_booking) return false;
          return new Date(b.date_of_booking) >= fromDate;
        });
      }
      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59);
        filtered = filtered.filter(b => {
          if (!b.date_of_booking) return false;
          return new Date(b.date_of_booking) <= toDate;
        });
      }
    }
    if (filters.bookingNo) {
      filtered = filtered.filter(b => 
        b.booking_no?.toString().includes(filters.bookingNo)
      );
    }
    if (filters.customerName) {
      const search = filters.customerName.toLowerCase();
      filtered = filtered.filter(b => 
        `${b.customer_first_name} ${b.customer_last_name}`.toLowerCase().includes(search)
      );
    }
    if (filters.mobile) {
      filtered = filtered.filter(b => b.mobile_no_1?.includes(filters.mobile));
    }
    // New filters
    if (filters.registrationNo) {
      filtered = filtered.filter(b => 
        b.registration_number?.toLowerCase().includes(filters.registrationNo.toLowerCase())
      );
    }
    if (filters.chassisNo) {
      filtered = filtered.filter(b => 
        b.chassis_no?.toLowerCase().includes(filters.chassisNo.toLowerCase())
      );
    }
    if (filters.vehicleSearch) {
      const search = filters.vehicleSearch.toLowerCase();
      filtered = filtered.filter(b => 
        `${b.model || ''} ${b.variant || ''} ${b.color || ''}`.toLowerCase().includes(search)
      );
    }
    // Engine/Motor No filter
    if (filters.engineMotorNo) {
      filtered = filtered.filter(b => 
        b.engine_motor_no?.toLowerCase().includes(filters.engineMotorNo.toLowerCase())
      );
    }
    // Charger No filter
    if (filters.chargerNo) {
      filtered = filtered.filter(b => 
        b.charger_no?.toLowerCase().includes(filters.chargerNo.toLowerCase())
      );
    }
    // Battery No filter
    if (filters.batteryNo) {
      filtered = filtered.filter(b => 
        b.battery_no?.toLowerCase().includes(filters.batteryNo.toLowerCase())
      );
    }
    // Sales Rep filter
    if (filters.salesRep) {
      filtered = filtered.filter(b => 
        b.sales_representative?.toLowerCase().includes(filters.salesRep.toLowerCase())
      );
    }
    if (filters.brands.length > 0) {
      filtered = filtered.filter(b => filters.brands.includes(b.brand_name));
    }
    if (filters.models.length > 0) {
      filtered = filtered.filter(b => filters.models.includes(b.model));
    }
    if (filters.salesReps.length > 0) {
      filtered = filtered.filter(b => filters.salesReps.includes(b.sales_representative));
    }

    setFilteredBookings(filtered);
    setCurrentPage(1);
  };

  // Clear other search fields when entering a new one
  const handleFilterChange = (key, value) => {
    const searchFields = ['bookingNo', 'customerName', 'mobile', 'registrationNo', 'chassisNo', 'vehicleSearch', 'engineMotorNo', 'chargerNo', 'batteryNo', 'salesRep'];
    if (searchFields.includes(key) && value) {
      // Clear other search fields when typing in a new one
      const clearedFilters = { ...filters };
      searchFields.forEach(field => {
        if (field !== key) clearedFilters[field] = '';
      });
      clearedFilters[key] = value;
      setFilters(clearedFilters);
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    const defaultDateRange = getDefaultDateRange();
    setFilters({
      dateFrom: defaultDateRange.from,
      dateTo: defaultDateRange.to,
      bookingNo: '',
      customerName: '',
      mobile: '',
      registrationNo: '',
      chassisNo: '',
      engineMotorNo: '',
      chargerNo: '',
      batteryNo: '',
      salesRep: '',
      vehicleSearch: '',
      brands: [],
      models: [],
      salesReps: []
    });
    setSelectAllBookings(false);
  };

  // Check if any filter is active
  const hasActiveFilters = () => {
    return filters.bookingNo || filters.customerName || filters.mobile || 
           filters.registrationNo || filters.chassisNo || filters.engineMotorNo ||
           filters.chargerNo || filters.batteryNo || filters.salesRep || filters.vehicleSearch;
  };

  // Indian currency formatter
  const formatIndianCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    const num = parseFloat(amount);
    if (isNaN(num)) return '-';
    return '₹' + num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  const toggleColumn = (columnId) => {
    setSelectedColumns(prev => {
      if (prev.includes(columnId)) {
        return prev.filter(c => c !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  const selectAllColumns = () => setSelectedColumns(ALL_COLUMNS.map(c => c.id));
  const selectDefaultColumns = () => setSelectedColumns(ALL_COLUMNS.filter(c => c.default).map(c => c.id));

  // Inline Edit Functions
  const startEdit = (bookingId, field, currentValue) => {
    setEditingCell({ bookingId, field });
    setEditValue(currentValue || '');
  };

  const saveEdit = async () => {
    if (!editingCell) return;
    
    try {
      await axios.put(`${BACKEND_URL}/api/bookings/${editingCell.bookingId}`, {
        [editingCell.field]: editValue
      }, getAuthHeader());
      
      setAllBookings(prev => prev.map(b => 
        b.id === editingCell.bookingId ? { ...b, [editingCell.field]: editValue } : b
      ));
      toast.success('Updated successfully');
    } catch (error) {
      toast.error('Failed to update');
    }
    setEditingCell(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Bulk Edit
  const handleBulkEdit = async () => {
    if (!bulkEditField || selectedBookings.length === 0) {
      toast.error('Select field and bookings');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/bookings/bulk-edit`, {
        booking_ids: selectedBookings,
        field: bulkEditField,
        value: bulkEditValue
      }, getAuthHeader());
      
      toast.success(`Updated ${selectedBookings.length} bookings`);
      setShowBulkEditModal(false);
      setBulkEditField('');
      setBulkEditValue('');
      setSelectedBookings([]);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk update failed');
    }
  };

  // Export Functions
  const getExportData = () => {
    const fields = exportType === 'all' ? ALL_COLUMNS.map(c => c.id) : exportFields;
    return filteredBookings.map(b => {
      const row = {};
      fields.forEach(f => {
        if (f === 'customer_name') {
          row[f] = `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim();
        } else if (f === 'vehicle') {
          row[f] = `${b.model || ''} ${b.variant || ''} ${b.color || ''}`.trim();
        } else {
          row[f] = b[f] || '';
        }
      });
      return row;
    });
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Generating Excel...');
      const fields = exportType === 'all' ? ALL_COLUMNS.map(c => c.id) : exportFields;
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      const response = await axios.post(`${BACKEND_URL}/api/bookings/export-excel-custom`, {
        booking_ids: filteredBookings.map(b => b.id),
        fields: fields
      }, { 
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Bookings_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleExportPDF = () => {
    try {
      toast.info('Generating PDF...');
      const doc = new jsPDF('l', 'mm', 'a4');
      const fields = exportType === 'all' ? ALL_COLUMNS : ALL_COLUMNS.filter(c => exportFields.includes(c.id));
      
      doc.setFontSize(14);
      doc.text('Bookings Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString()} | Total: ${filteredBookings.length} records`, 14, 22);

      const tableData = filteredBookings.map(b => 
        fields.map(f => {
          if (f.id === 'customer_name') return `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim();
          if (f.id === 'vehicle') return `${b.model || ''} ${b.variant || ''}`.trim();
          if (f.id === 'date_of_booking' || f.id === 'dms_invoice_date') return formatDate(b[f.id]);
          return b[f.id] || '-';
        })
      );

      doc.autoTable({
        head: [fields.map(f => f.label)],
        body: tableData,
        startY: 28,
        styles: { fontSize: 7, cellPadding: 1 },
        headStyles: { fillColor: [59, 130, 246], fontSize: 7 }
      });

      doc.save(`Bookings_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrint = () => {
    handleExportPDF();
  };

  // Email share with attachment via backend
  const handleEmailShare = async () => {
    if (!shareEmail) {
      toast.error('Enter email address');
      return;
    }
    
    try {
      setSendingEmail(true);
      toast.info('Sending email with attachment...');
      
      const fields = exportType === 'all' ? ALL_COLUMNS.map(c => c.id) : exportFields;
      
      const response = await axios.post(`${BACKEND_URL}/api/bookings/share-email`, {
        email: shareEmail,
        booking_ids: filteredBookings.map(b => b.id),
        fields: fields
      }, getAuthHeader());
      
      if (response.data.status === 'success') {
        toast.success('Email sent successfully with attachment!');
        setShowExportModal(false);
        setShareEmail('');
      } else if (response.data.status === 'skipped') {
        toast.warning('Email service not configured. Please contact admin.');
      } else {
        toast.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // WhatsApp share with download link
  const handleWhatsAppShare = async () => {
    try {
      setGeneratingLink(true);
      toast.info('Generating share link...');
      
      const fields = exportType === 'all' ? ALL_COLUMNS.map(c => c.id) : exportFields;
      
      const response = await axios.post(`${BACKEND_URL}/api/bookings/generate-share-link`, {
        booking_ids: filteredBookings.map(b => b.id),
        fields: fields
      }, getAuthHeader());
      
      const shareLink = response.data.share_link;
      const summary = response.data.summary;
      
      const text = encodeURIComponent(
        `📋 *Bookings Report*\n` +
        `📅 Date: ${new Date().toLocaleDateString()}\n\n` +
        `${summary}\n\n` +
        `📥 Download Report:\n${shareLink}\n\n` +
        `(Link expires in 24 hours)`
      );
      
      const url = sharePhone 
        ? `https://web.whatsapp.com/send?phone=${sharePhone}&text=${text}`
        : `https://web.whatsapp.com/send?text=${text}`;
      
      { const _a = document.createElement('a'); _a.href = url; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
      toast.success('WhatsApp opened with download link!');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to generate share link');
    } finally {
      setGeneratingLink(false);
    }
  };

  // Render cell value
  const renderCell = (booking, columnId) => {
    const isEditing = editingCell?.bookingId === booking.id && editingCell?.field === columnId;
    
    if (isEditing) {
      return (
        <div className="flex gap-1">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full p-1 border rounded text-xs"
            autoFocus
          />
          <button onClick={saveEdit} className="text-green-600 text-xs">✓</button>
          <button onClick={cancelEdit} className="text-red-600 text-xs">✗</button>
        </div>
      );
    }

    let value;
    // Amount/price fields for Indian currency formatting
    const currencyFields = ['customer_balance', 'on_road_price', 'ex_showroom_price', 'pool_amount', 
                           'scheme_discount_1', 'scheme_discount_2', 'special_discount'];
    
    switch (columnId) {
      case 'booking_no':
        return (
          <button 
            onClick={() => handleBookingNoClick(booking)}
            className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
            title="Click for options"
          >
            {booking.booking_no}
          </button>
        );
      case 'customer_name':
        value = `${booking.customer_first_name || ''} ${booking.customer_last_name || ''}`.trim();
        break;
      case 'vehicle':
        value = `${booking.model || ''} ${booking.variant || ''} ${booking.color || ''}`.trim();
        break;
      case 'date_of_booking':
      case 'dms_invoice_date':
      case 'delivery_date':
      case 'date_of_registration':
      case 'hsrp_informed_date':
      case 'pme_submission_date':
        value = formatDate(booking[columnId]);
        break;
      case 'customer_balance':
        const balance = parseFloat(booking.customer_balance) || 0;
        return (
          <span className={`font-medium ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {formatIndianCurrency(balance)}
          </span>
        );
      case 'on_road_price':
      case 'ex_showroom_price':
      case 'pool_amount':
      case 'scheme_discount_1':
      case 'scheme_discount_2':
      case 'special_discount':
        return <span>{formatIndianCurrency(booking[columnId])}</span>;
      case 'vendors_purchase_price': {
        const v = booking.vendors_purchase_price;
        if (v === null || v === undefined || v === '') return <span>-</span>;
        const n = Number(v);
        if (Number.isNaN(n)) return <span>-</span>;
        return <span>{'₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
      }
      default:
        value = booking[columnId] || '-';
    }

    return (
      <span 
        className="cursor-pointer hover:bg-blue-50 px-1 rounded"
        onDoubleClick={() => startEdit(booking.id, columnId, booking[columnId])}
        title="Double-click to edit"
      >
        {value}
      </span>
    );
  };

  // Pagination
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const displayedBookings = filteredBookings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleSelectAll = () => {
    if (selectedBookings.length === displayedBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(displayedBookings.map(b => b.id));
    }
  };

  const toggleSelectBooking = (id) => {
    setSelectedBookings(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Get unique values for filters
  const getUniqueValues = (field) => {
    const values = new Set();
    allBookings.forEach(b => { if (b[field]) values.add(b[field]); });
    return Array.from(values).sort();
  };

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="All Bookings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="All Bookings">
      <div className="space-y-4">
        {/* Month Tabs */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="flex border-b">
            <button
              onClick={() => handleMonthTabChange('current')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeMonthTab === 'current'
                  ? 'text-white border-b-2 border-violet-700'
                  : 'text-gray-600 hover:bg-indigo-50/60'
              }`}
              style={activeMonthTab === 'current' ? { background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' } : {}}
            >
              Current Month ({getMonthName(0)})
            </button>
            <button
              onClick={() => handleMonthTabChange('previous')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeMonthTab === 'previous'
                  ? 'text-white border-b-2 border-violet-700'
                  : 'text-gray-600 hover:bg-indigo-50/60'
              }`}
              style={activeMonthTab === 'previous' ? { background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' } : {}}
            >
              Previous Month ({getMonthName(-1)})
            </button>
          </div>
        </div>

        {/* Column Selector Bar */}
        <div className="bg-white/80 backdrop-blur-xl p-4 rounded-2xl border border-indigo-100/60 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                className="px-4 py-2 text-white rounded-xl text-sm font-medium hidden md:block shadow-[0_4px_12px_rgba(79,70,229,0.30)] hover:shadow-[0_8px_20px_rgba(79,70,229,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
                style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' }}
              >
                {showColumnSelector ? 'Hide Columns' : 'Select Columns'}
              </button>
              <span className="text-sm text-slate-600 font-medium">
                Showing {filteredBookings.length} of {allBookings.length} bookings
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => navigate('/bookings/new')}
                className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 text-sm font-medium transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
              >
                + New Booking
              </button>
              <button
                onClick={() => { setExportFields(selectedColumns); setShowExportModal(true); }}
                className="px-4 py-2 text-white rounded-xl text-sm font-medium hidden md:block transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 shadow-[0_4px_12px_rgba(124,58,237,0.30)]"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #9333EA 100%)' }}
              >
                Export / Share
              </button>
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-800 text-sm font-medium hidden md:block transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
              >
                Print
              </button>
              {selectedBookings.length > 0 && (
                <button
                  onClick={() => setShowBulkEditModal(true)}
                  className="px-4 py-2 text-white rounded-xl text-sm font-medium transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 shadow-[0_4px_12px_rgba(217,119,6,0.30)]"
                  style={{ background: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)' }}
                >
                  Bulk Edit ({selectedBookings.length})
                </button>
              )}
            </div>
          </div>

          {/* Column Selection Grid */}
          {showColumnSelector && (
            <div className="mt-4 pt-4 border-t border-indigo-100/60">
              <div className="flex gap-2 mb-3">
                <button onClick={selectAllColumns} className="px-3 py-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-lg hover:bg-indigo-100 transition-colors font-medium">
                  Select All
                </button>
                <button onClick={selectDefaultColumns} className="px-3 py-1 text-xs bg-slate-50 text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors font-medium">
                  Default Columns
                </button>
              </div>
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {[...ALL_COLUMNS].sort((a, b) => a.label.localeCompare(b.label)).map(col => (
                  <label key={col.id} className={`flex items-center gap-1 p-2 rounded-lg cursor-pointer text-xs transition-colors ${
                    selectedColumns.includes(col.id) ? 'bg-indigo-50 border-indigo-300' : 'bg-slate-50 border-slate-200'
                  } border hover:bg-indigo-100/60`}>
                    <input
                      type="checkbox"
                      checked={selectedColumns.includes(col.id)}
                      onChange={() => toggleColumn(col.id)}
                      className="w-3 h-3"
                    />
                    <span className="truncate">{col.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Filters - Redesigned Layout */}
        <div className="bg-white p-4 rounded-xl border shadow-sm">
          {/* Row 1: Date Range (right aligned) + Select All Checkbox */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectAllBookings}
                onChange={(e) => setSelectAllBookings(e.target.checked)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm font-medium text-gray-700">Select All Bookings (ignore date filter)</span>
            </label>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-600">From (Booking Date):</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="p-1.5 border rounded text-sm w-32"
                  disabled={selectAllBookings}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-600">To (Booking Date):</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="p-1.5 border rounded text-sm w-32"
                  disabled={selectAllBookings}
                />
              </div>
            </div>
          </div>

          {/* Row 2: Multi-Filter Options - All in one row with cross icons */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Filter: B.No */}
            <div className="relative">
              <input
                type="text"
                placeholder="B.No"
                value={filters.bookingNo}
                onChange={(e) => handleFilterChange('bookingNo', e.target.value)}
                className="w-20 p-1.5 pr-6 border rounded text-sm"
              />
              {filters.bookingNo && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, bookingNo: '' }))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter: Customer Name */}
            <div className="relative">
              <input
                type="text"
                placeholder="Customer Name"
                value={filters.customerName}
                onChange={(e) => handleFilterChange('customerName', e.target.value)}
                className="w-32 p-1.5 pr-6 border rounded text-sm"
              />
              {filters.customerName && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, customerName: '' }))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter: Mobile */}
            <div className="relative">
              <input
                type="text"
                placeholder="Mobile"
                value={filters.mobile}
                onChange={(e) => handleFilterChange('mobile', e.target.value)}
                className="w-28 p-1.5 pr-6 border rounded text-sm"
              />
              {filters.mobile && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, mobile: '' }))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter: Registration No */}
            <div className="relative">
              <input
                type="text"
                placeholder="Reg. No"
                value={filters.registrationNo}
                onChange={(e) => handleFilterChange('registrationNo', e.target.value)}
                className="w-28 p-1.5 pr-6 border rounded text-sm"
              />
              {filters.registrationNo && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, registrationNo: '' }))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter: Sales Rep */}
            <div className="relative">
              <input
                type="text"
                placeholder="Sales Rep"
                value={filters.salesRep}
                onChange={(e) => handleFilterChange('salesRep', e.target.value)}
                className="w-28 p-1.5 pr-6 border rounded text-sm"
              />
              {filters.salesRep && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, salesRep: '' }))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter: Chassis No */}
            <div className="relative">
              <input
                type="text"
                placeholder="Chassis No"
                value={filters.chassisNo}
                onChange={(e) => handleFilterChange('chassisNo', e.target.value)}
                className="w-28 p-1.5 pr-6 border rounded text-sm"
              />
              {filters.chassisNo && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, chassisNo: '' }))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter: Engine/Motor No */}
            <div className="relative">
              <input
                type="text"
                placeholder="Engine/Motor No"
                value={filters.engineMotorNo}
                onChange={(e) => handleFilterChange('engineMotorNo', e.target.value)}
                className="w-32 p-1.5 pr-6 border rounded text-sm"
              />
              {filters.engineMotorNo && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, engineMotorNo: '' }))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter: Charger No */}
            <div className="relative">
              <input
                type="text"
                placeholder="Charger No"
                value={filters.chargerNo}
                onChange={(e) => handleFilterChange('chargerNo', e.target.value)}
                className="w-28 p-1.5 pr-6 border rounded text-sm"
              />
              {filters.chargerNo && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, chargerNo: '' }))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter: Battery No */}
            <div className="relative">
              <input
                type="text"
                placeholder="Battery No"
                value={filters.batteryNo}
                onChange={(e) => handleFilterChange('batteryNo', e.target.value)}
                className="w-28 p-1.5 pr-6 border rounded text-sm"
              />
              {filters.batteryNo && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, batteryNo: '' }))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Reset Filters Button */}
            {hasActiveFilters() && (
              <button
                onClick={handleResetFilters}
                className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded text-sm font-medium hover:bg-red-100 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Unified Card Grid View - 2-Row Layout for ALL Screen Sizes */}
        <div>
          {/* Select All Header */}
          <div className="bg-white rounded-xl border shadow-sm p-3 mb-3 flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={displayedBookings.length > 0 && selectedBookings.length === displayedBookings.length}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm font-medium text-gray-700">Select All on Page</span>
            </label>
            <span className="text-sm text-gray-500">
              Page {currentPage} of {totalPages} ({filteredBookings.length} total)
            </span>
          </div>

          {/* Card Grid - Responsive: 1 col mobile, 2 cols landscape/tablet, 3 cols desktop */}
          {displayedBookings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-indigo-600">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h18M9 21V9" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-800 tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>No bookings yet</h3>
              <p className="text-sm text-slate-500 mt-1.5 max-w-md">Try adjusting filters or create a new booking. Once you start, all your bookings will appear here with rich filters and exports.</p>
              <button onClick={() => navigate('/bookings/new')} className="mt-5 px-5 py-2.5 text-white rounded-xl text-sm font-medium shadow-[0_4px_12px_rgba(79,70,229,0.30)] hover:shadow-[0_8px_20px_rgba(79,70,229,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' }} data-testid="empty-create-booking">
                + Create First Booking
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 anim-grid-stagger">
              {displayedBookings.map((booking, index) => {
                const serialNo = (currentPage - 1) * itemsPerPage + index + 1;
                const balance = parseFloat(booking.customer_balance) || 0;
                return (
                  <div 
                    key={booking.id} 
                    className={`bg-white/80 backdrop-blur-xl rounded-2xl border border-indigo-100/50 shadow-sm overflow-hidden hover:shadow-[0_10px_30px_-8px_rgba(79,70,229,0.20)] hover:-translate-y-0.5 transition-all duration-240 ease-out ${
                      index % 3 === 0 ? 'border-l-4 border-l-indigo-500' : 
                      index % 3 === 1 ? 'border-l-4 border-l-violet-500' : 'border-l-4 border-l-fuchsia-500'
                    }`}
                  >
                    {/* Row 1: Header with Serial, Booking Info, Status */}
                    <div className="p-3 sm:p-4 border-b border-indigo-50 bg-gradient-to-r from-indigo-50/40 via-violet-50/30 to-white">
                      <div className="flex items-start gap-3">
                        {/* Serial Number Badge */}
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-[0_4px_12px_rgba(79,70,229,0.30)]" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' }}>
                          {serialNo}
                        </div>
                        
                        {/* Main Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => handleBookingNoClick(booking)}
                              className="font-bold text-indigo-700 hover:text-indigo-900 hover:underline text-lg"
                            >
                              #{booking.booking_no}
                            </button>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              booking.registration_number ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {booking.registration_number ? 'Registered' : 'Pending'}
                            </span>
                            {booking.loan_status && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                booking.loan_status === 'Cash' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {booking.loan_status}
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-gray-800 mt-1 text-base">
                            {booking.customer_first_name} {booking.customer_last_name || ''}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {booking.brand_name} <span className="font-medium">{booking.model}</span> {booking.variant && `- ${booking.variant}`}
                            {booking.color && <span className="ml-1 text-gray-500">({booking.color})</span>}
                          </p>
                        </div>

                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={selectedBookings.includes(booking.id)}
                          onChange={() => toggleSelectBooking(booking.id)}
                          className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1"
                        />
                      </div>
                    </div>

                    {/* Row 2: Details Grid - Responsive for all screen sizes */}
                    <div className="p-3 sm:p-4 bg-gray-50">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm">
                        <div>
                          <span className="text-gray-400 text-xs block">Mobile</span>
                          <a href={`tel:${booking.mobile_no_1}`} className="text-blue-600 font-medium hover:underline">
                            {booking.mobile_no_1 || '-'}
                          </a>
                        </div>
                        <div>
                          <span className="text-gray-400 text-xs block">Booking Date</span>
                          <span className="text-gray-700 font-medium">
                            {booking.date_of_booking ? new Date(booking.date_of_booking).toLocaleDateString('en-GB') : '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400 text-xs block">Chassis No</span>
                          <span className="text-gray-700 font-medium text-xs">{booking.chassis_no || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400 text-xs block">Balance</span>
                          <span className={`font-semibold ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatIndianCurrency(balance)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400 text-xs block">Sales Rep</span>
                          <span className="text-gray-700 font-medium truncate block">{booking.sales_representative || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400 text-xs block">Registration No</span>
                          <span className="text-gray-700 font-medium">{booking.registration_number || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400 text-xs block">DMS Invoice</span>
                          <span className="text-gray-700 font-medium">
                            {booking.dms_invoice_date ? new Date(booking.dms_invoice_date).toLocaleDateString('en-GB') : '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400 text-xs block">PME Drive</span>
                          <span className="text-gray-700 font-medium">{booking.pme_drive_no || '-'}</span>
                        </div>
                      </div>
                      
                      {/* Actions Row */}
                      <div className="flex gap-2 mt-4 pt-3 border-t border-gray-200">
                        <button
                          onClick={() => {
                            const returnUrl = encodeURIComponent(location.pathname + location.search);
                            navigate(`/sales/bookings/${booking.id}/view?returnTo=${returnUrl}`);
                          }}
                          className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
                        >
                          View Details
                        </button>
                        {canUpdate(RESOURCES.BOOKINGS) && (
                          <button
                            onClick={() => {
                              const returnUrl = encodeURIComponent(location.pathname + location.search);
                              navigate(`/sales/bookings/edit/${booking.id}?returnTo=${returnUrl}`);
                            }}
                            className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                          >
                            Edit
                          </button>
                        )}
                        <a
                          href={`tel:${booking.mobile_no_1}`}
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                        >
                          Call
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          <div className="bg-white rounded-xl border shadow-sm p-4 mt-4">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredBookings.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              showInfo={true}
            />
          </div>
        </div>

        {/* Bulk Edit Modal */}
        {showBulkEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold mb-4">Bulk Edit ({selectedBookings.length} bookings)</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Select Field to Update</label>
                  <select
                    value={bulkEditField}
                    onChange={(e) => { setBulkEditField(e.target.value); setBulkEditValue(''); }}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="">-- Select Field --</option>
                    {BULK_EDIT_FIELDS.map(f => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </select>
                </div>

                {bulkEditField && (
                  <div>
                    <label className="block text-sm font-medium mb-2">New Value</label>
                    {(() => {
                      const field = BULK_EDIT_FIELDS.find(f => f.id === bulkEditField);
                      if (!field) return null;
                      
                      if (field.type === 'select') {
                        return (
                          <select
                            value={bulkEditValue}
                            onChange={(e) => setBulkEditValue(e.target.value)}
                            className="w-full p-2 border rounded-lg"
                          >
                            <option value="">-- Select --</option>
                            {field.options.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        );
                      } else if (field.type === 'date') {
                        return (
                          <input
                            type="date"
                            value={bulkEditValue}
                            onChange={(e) => setBulkEditValue(e.target.value)}
                            className="w-full p-2 border rounded-lg"
                          />
                        );
                      } else if (field.type === 'number') {
                        return (
                          <input
                            type="number"
                            value={bulkEditValue}
                            onChange={(e) => setBulkEditValue(e.target.value)}
                            className="w-full p-2 border rounded-lg"
                            placeholder="Enter amount"
                          />
                        );
                      } else if (field.type === 'master') {
                        return (
                          <select
                            value={bulkEditValue}
                            onChange={(e) => setBulkEditValue(e.target.value)}
                            className="w-full p-2 border rounded-lg"
                          >
                            <option value="">-- Select --</option>
                            {masters[field.masterKey]?.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        );
                      } else {
                        return (
                          <input
                            type="text"
                            value={bulkEditValue}
                            onChange={(e) => setBulkEditValue(e.target.value)}
                            className="w-full p-2 border rounded-lg"
                            placeholder="Enter value"
                          />
                        );
                      }
                    })()}
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => { setShowBulkEditModal(false); setBulkEditField(''); setBulkEditValue(''); }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkEdit}
                  disabled={!bulkEditField || !bulkEditValue}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400"
                >
                  Update All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold mb-4">Export & Share Bookings</h3>
              
              <div className="space-y-4">
                {/* Export Type */}
                <div>
                  <label className="block text-sm font-medium mb-2">Export Fields</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setExportType('selected')}
                      className={`flex-1 p-3 border rounded-lg ${exportType === 'selected' ? 'bg-blue-100 border-blue-500' : ''}`}
                    >
                      Selected Columns ({selectedColumns.length})
                    </button>
                    <button
                      onClick={() => setExportType('all')}
                      className={`flex-1 p-3 border rounded-lg ${exportType === 'all' ? 'bg-blue-100 border-blue-500' : ''}`}
                    >
                      All Fields ({ALL_COLUMNS.length})
                    </button>
                  </div>
                </div>

                {/* Custom Field Selection for 'selected' type */}
                {exportType === 'selected' && (
                  <div>
                    <div className="flex gap-2 mb-2">
                      <button 
                        onClick={() => setExportFields(ALL_COLUMNS.map(c => c.id))}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded"
                      >
                        Select All
                      </button>
                      <button 
                        onClick={() => setExportFields(selectedColumns)}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                      >
                        Current View
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                      {ALL_COLUMNS.map(col => (
                        <label key={col.id} className={`flex items-center gap-2 p-1 rounded text-xs cursor-pointer ${
                          exportFields.includes(col.id) ? 'bg-blue-50' : ''
                        }`}>
                          <input
                            type="checkbox"
                            checked={exportFields.includes(col.id)}
                            onChange={() => {
                              setExportFields(prev => 
                                prev.includes(col.id) ? prev.filter(f => f !== col.id) : [...prev, col.id]
                              );
                            }}
                            className="w-3 h-3"
                          />
                          {col.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Export */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Quick Export</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={handleExportExcel} className="p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                      📊 Download Excel
                    </button>
                    <button onClick={handleExportPDF} className="p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                      📄 Download PDF
                    </button>
                  </div>
                </div>

                {/* Email Share */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Share via Email</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="Enter email address"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      className="flex-1 p-2 border rounded-lg text-sm"
                    />
                    <button 
                      onClick={handleEmailShare} 
                      disabled={sendingEmail}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:bg-blue-400"
                    >
                      {sendingEmail ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Email will include Excel report as attachment with HTML preview</p>
                </div>

                {/* WhatsApp Share */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Share via WhatsApp</p>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={sharePhone}
                      onChange={(e) => setSharePhone(e.target.value)}
                      className="flex-1 p-2 border rounded-lg text-sm"
                    />
                    <button 
                      onClick={handleWhatsAppShare} 
                      disabled={generatingLink}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm disabled:bg-green-400"
                    >
                      {generatingLink ? 'Generating...' : 'WhatsApp'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Includes download link (expires in 24 hours)</p>
                </div>
              </div>

              <button
                onClick={() => setShowExportModal(false)}
                className="w-full mt-4 px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Booking Action Modal - Edit/View/Delete */}
        {showActionModal && selectedActionBooking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-lg font-bold text-gray-800">Booking #{selectedActionBooking.booking_no}</h3>
                <p className="text-sm text-gray-500">
                  {selectedActionBooking.customer_first_name} {selectedActionBooking.customer_last_name}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {selectedActionBooking.model} {selectedActionBooking.variant}
                </p>
              </div>
              
              <div className="space-y-3">
                {canUpdate(RESOURCES.BOOKINGS) && (
                  <button
                    onClick={() => { 
                      setShowActionModal(false); 
                      const returnUrl = encodeURIComponent(location.pathname + location.search);
                      navigate(`/sales/bookings/edit/${selectedActionBooking.id}?returnTo=${returnUrl}`); 
                    }}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
                  >
                    <span>✏️</span> Edit Booking
                  </button>
                )}
                <button
                  onClick={() => { 
                    setShowActionModal(false); 
                    const returnUrl = encodeURIComponent(location.pathname + location.search);
                    navigate(`/sales/bookings/${selectedActionBooking.id}/view?returnTo=${returnUrl}`); 
                  }}
                  className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2"
                >
                  <span>👁️</span> View Details
                </button>
                {canDelete(RESOURCES.BOOKINGS) && (
                  <button
                    onClick={() => handleDeleteBooking(selectedActionBooking.id)}
                    className="w-full py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center justify-center gap-2"
                  >
                    <span>🗑️</span> Delete Booking
                  </button>
                )}
              </div>

              <button
                onClick={() => { setShowActionModal(false); setSelectedActionBooking(null); }}
                className="w-full mt-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </SalesLayout>
  );
}

export default EnhancedBookingsList;
